import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBibleBootstrapStore } from '@/lib/bsb-progress-store';
import styles from './BibleBootstrapIndicator.module.css';

/**
 * Reverent indicator shown while the local-first BSB hydration runs
 * (M.3.6.3.b). Mirrors GatheringIndicator's visual register: soft gold
 * breathing dot + italic text, no spinner, no progress bar, no visible
 * percentage. Hydration is gated behind explicit caller invocation;
 * not wired in main.tsx this chunk.
 *
 * 500ms render delay so fast hydrations (cached, network-first hits)
 * never flash UI on screen. `delayElapsed` resets to false at the
 * start of every isBibleBootstrapping=true cycle so each hydration
 * gets its own fresh 500ms gate, including a re-hydrate triggered by
 * a sha mismatch later in the same component lifetime.
 */
const RENDER_DELAY_MS = 500;

export default function BibleBootstrapIndicator() {
  const { t } = useTranslation();
  const isBibleBootstrapping = useBibleBootstrapStore(
    (s) => s.isBibleBootstrapping,
  );
  const [delayElapsed, setDelayElapsed] = useState(false);

  useEffect(() => {
    if (!isBibleBootstrapping) return;
    // Canonical "reset state when key prop changes" idiom: see
    // PersonKeepsakeSheet.tsx for the matching precedent. The
    // setState fires at the start of each true cycle so a second
    // hydration in the same component lifetime gets a fresh delay.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDelayElapsed(false);
    const handle = setTimeout(() => setDelayElapsed(true), RENDER_DELAY_MS);
    return () => clearTimeout(handle);
  }, [isBibleBootstrapping]);

  if (!isBibleBootstrapping || !delayElapsed) return null;

  return (
    <div role="status" aria-live="polite" className={styles.indicator}>
      <span aria-hidden="true" className={styles.dot} />
      <span className={styles.text}>{t('bible.preparing')}</span>
    </div>
  );
}
